import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, Typography, Box, IconButton, Alert, CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { employeePayrollAPI } from '../utils/api';
import type { EmployeePortalException } from '../types/payroll';
import { validateAbsenceAttachment } from './AbsenceReasonModal';
import { formatDateOnly } from '../utils/timezone';

const NOTE_MAX_LENGTH = 1000; // Keep in sync with backend validation

interface Props {
  open: boolean;
  onClose: () => void;
  exception: EmployeePortalException | null;
  onSuccess: () => void;
}

const EmployeePayrollExceptionExplainModal: React.FC<Props> = ({ open, onClose, exception, onSuccess }) => {
  const { t, i18n } = useTranslation('auth', { keyPrefix: 'employeeDashboard.payroll.exceptions' });
  const { t: tc } = useTranslation('common');
  const [note, setNote] = useState('');
  const [reasonType, setReasonType] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNote('');
      setReasonType('');
      setAttachment(null);
      setError(null);
    }
  }, [open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateAbsenceAttachment(file);
    if (validation === 'tooLarge') { setError(t('fileTooLarge')); return; }
    if (validation === 'invalidType') { setError(t('invalidFileType')); return; }
    setError(null);
    setAttachment(file);
  };

  const handleSubmit = async () => {
    if (!exception) return;
    if (!note.trim()) { setError(t('noteRequired')); return; }
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('note', note.trim());
      if (reasonType) fd.append('reason_type', reasonType);
      if (attachment) fd.append('attachment', attachment);
      const res = await employeePayrollAPI.explainMyPayrollException(exception.id, fd as any);
      if (res.success) {
        onSuccess();
        onClose();
      } else {
        setError((res as any).message || t('submitFailed'));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.errors?.note?.[0] || t('submitFailed');
      setError(typeof msg === 'string' ? msg : t('submitFailed'));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{t('explainTitle')}</Typography>
        <IconButton onClick={onClose} size="small" aria-label={tc('buttons.close')}><i className="bi bi-x-lg" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {exception && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">{t(`types.${exception.type}`)} – {formatDateOnly(exception.work_date, i18n.language)}</Typography>
          </Box>
        )}
        <TextField
          fullWidth
          multiline
          rows={4}
          label={t('note')}
          value={note}
          onChange={e => setNote(e.target.value)}
          required
          inputProps={{ maxLength: NOTE_MAX_LENGTH }}
          helperText={`${note.length}/${NOTE_MAX_LENGTH}`}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('reasonType')}</InputLabel>
          <Select value={reasonType} label={t('reasonType')} onChange={e => setReasonType(e.target.value)}>
            <MenuItem value=""><em>—</em></MenuItem>
            <MenuItem value="sick_leave">{t('sickLeave')}</MenuItem>
            <MenuItem value="out_of_office">{t('outOfOffice')}</MenuItem>
            <MenuItem value="emergency_leave">{t('emergencyLeave')}</MenuItem>
            <MenuItem value="custom">{t('custom')}</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant="outlined" component="label" startIcon={<i className="bi bi-upload" />}>
            {t('attachment')}
            <input type="file" hidden accept=".jpg,.jpeg,.png,.pdf" onChange={handleFileChange} />
          </Button>
          {attachment ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachment.name}</Typography>
              <IconButton size="small" onClick={() => setAttachment(null)} aria-label={tc('buttons.delete')}><i className="bi bi-x" /></IconButton>
            </Box>
          ) : <Typography variant="caption" color="text.secondary">{t('noFileChosen')}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{tc('buttons.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : undefined}>
          {loading ? tc('loading.saving') : t('submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeePayrollExceptionExplainModal;
